/**
 * Entry point for the rebuilt filters experience.
 * Loads config from PHP, hydrates the store, and mounts feature modules.
 *
 * @typedef {import('./types').FiltersConfig} FiltersConfig
 */

import { createFiltersStore } from './state/store.js';
import { bootstrapDialogs } from './dialog/controller.js';
import { registerQuickChipsModule } from './modules/quick-chips.js';
import { registerMorePanelModule } from './modules/more-panel.js';
import { registerMatchupsModule } from './modules/matchups.js';
import { registerFiltersPanelModule } from './modules/filters-panel.js';

/**
 * Initialise the filters UI using server-provided config.
 * @param {FiltersConfig} config
 */
export function initFiltersUI(config) {
  if (!config || typeof config !== 'object') {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[filters] Missing config payload; aborting init');
    }
    return;
  }

  const store = createFiltersStore(config);

  if (typeof window !== 'undefined') {
    window.propsFilters = window.propsFilters || {};
    window.propsFilters.store = store;
  }

  bootstrapDialogs(store);

  registerQuickChipsModule(store);
  registerMorePanelModule(store);
  registerMatchupsModule(store);
  registerFiltersPanelModule(store);
}

/**
 * Auto-discover config script tag and initialise immediately when present.
 */
export function autoInitFromDom() {
  const script = document.querySelector('script[data-props-filters-config]');
  if (!script) {
    return;
  }

  try {
    const payload = JSON.parse(script.textContent || '{}');
    initFiltersUI(payload);
  } catch (error) {
    console.error('[filters] Failed to parse config payload', error);
  }
}

if (typeof window !== 'undefined') {
  window.propsFilters = window.propsFilters || {
    init: initFiltersUI,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInitFromDom);
  } else {
    autoInitFromDom();
  }
}
